import React, { useState, useEffect } from 'react';
import { TrendingUp, Award, Edit3, CheckCircle, RefreshCw, BarChart2 } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const CommissionManagement = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLevel, setEditingLevel] = useState(null);
  const [editForm, setEditForm] = useState({ percentage: '', daily_bonus: '', monthly_bonus: '', event_bonus: '' });

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/business/commissions/settings`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setCommissions(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (level) => {
    const comm = commissions.find(c => c.level === level);
    if (comm) {
      setEditingLevel(level);
      setEditForm({
        percentage: comm.percentage,
        daily_bonus: comm.daily_bonus,
        monthly_bonus: comm.monthly_bonus,
        event_bonus: comm.event_bonus
      });
    }
  };

  const handleSaveCommission = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/commissions/settings`, {
        level: editingLevel,
        percentage: parseFloat(editForm.percentage),
        daily_bonus: parseFloat(editForm.daily_bonus),
        monthly_bonus: parseFloat(editForm.monthly_bonus),
        event_bonus: parseFloat(editForm.event_bonus)
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        alert("Commission level parameters updated successfully!");
        setEditingLevel(null);
        fetchCommissions();
      }
    } catch (err) {
      alert("Failed to save changes");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Commission & Bonus Configurations</h2>
          <p className="text-slate-500 mt-1">Configure baseline percentages, define daily & monthly active thresholds, and set event-specific bonuses.</p>
        </div>
        <button 
          onClick={fetchCommissions}
          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
        >
          Refresh Parameters
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Tier Configs */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden">
            <div className="p-6 border-b border-slate-50 flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <BarChart2 size={20} className="text-pink-600" /> Seller Tier Configurations
              </h3>
            </div>

            {loading ? (
              <div className="p-20 text-center">
                <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-500">Loading configurations...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                      <th className="p-6">Seller Tier</th>
                      <th className="p-6">Commission %</th>
                      <th className="p-6">Daily Bonus</th>
                      <th className="p-6">Monthly Target Bonus</th>
                      <th className="p-6">Event Bonus</th>
                      <th className="p-6 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {commissions.map((comm) => (
                      <tr key={comm.id} className="hover:bg-slate-50/50 transition">
                        <td className="p-6">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-black uppercase ${
                            comm.level === 'VIP' ? 'bg-purple-100 text-purple-700' :
                            comm.level === 'GOLD' ? 'bg-yellow-100 text-yellow-700' :
                            comm.level === 'SILVER' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {comm.level}
                          </span>
                        </td>
                        <td className="p-6 font-bold text-slate-800">{comm.percentage}%</td>
                        <td className="p-6 text-slate-600">${parseFloat(comm.daily_bonus).toFixed(2)}</td>
                        <td className="p-6 text-slate-600">${parseFloat(comm.monthly_bonus).toFixed(2)}</td>
                        <td className="p-6 text-slate-600">${parseFloat(comm.event_bonus).toFixed(2)}</td>
                        <td className="p-6 text-right">
                          <button 
                            onClick={() => handleEditClick(comm.level)}
                            className="p-2 hover:bg-slate-50 rounded-lg text-pink-600 transition"
                          >
                            <Edit3 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Edit Tier Parameters Column */}
        <div>
          {editingLevel ? (
            <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 animate-in slide-in-from-right-4 duration-300">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-slate-800 text-lg">Modify {editingLevel} Tier</h3>
                <button onClick={() => setEditingLevel(null)} className="text-slate-400 hover:text-slate-600">Cancel</button>
              </div>

              <form onSubmit={handleSaveCommission} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase">Commission Percentage (%)</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition font-bold"
                    value={editForm.percentage}
                    onChange={(e) => setEditForm({ ...editForm, percentage: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase">Daily Activity Bonus (USD)</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                    value={editForm.daily_bonus}
                    onChange={(e) => setEditForm({ ...editForm, daily_bonus: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase">Monthly Target Achievement Bonus (USD)</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                    value={editForm.monthly_bonus}
                    onChange={(e) => setEditForm({ ...editForm, monthly_bonus: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase">Event Participation Bonus (USD)</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    className="w-full mt-1.5 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                    value={editForm.event_bonus}
                    onChange={(e) => setEditForm({ ...editForm, event_bonus: e.target.value })}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl active:scale-95 transition shadow-lg shadow-pink-200"
                >
                  Save Tier Configurations
                </button>
              </form>
            </div>
          ) : (
            <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-3xl p-10 text-center text-slate-400 h-64 flex items-center justify-center">
              <div>
                <Award size={40} className="mx-auto mb-2 opacity-35" />
                <p className="text-sm font-semibold">Select a tier level to adjust its percentages and bonus options.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommissionManagement;
